import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Briefcase, Cog, Fuel, ShieldCheck, Images, ChevronLeft, ChevronRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url?.startsWith("http") ? url : `${BASE}${url}`);

export const FleetCard = ({ v, gallery, onReserve, onDetails }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [allChips, setAllChips] = useState(false);
  const images = gallery?.length ? gallery : [{ image_id: "hero", url: v.image, label: "Front" }];
  const img = images[Math.min(idx, images.length - 1)];
  const chips = allChips ? v.features : v.features.slice(0, 4);
  const std = v.insurance_tier === "standard";

  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden card-lift flex flex-col" data-testid={`fleet-card-${v.id}`}>
      <div className="px-4 pt-4 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <span className="bg-[#144733] text-white text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold" data-testid={`fleet-group-${v.id}`}>
            {t("Group")} {v.group}
          </span>
          <span className="bg-[#f0f5f0] text-[#144733] text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold border border-[#e2ebe4]">
            {v.group_label}
          </span>
        </span>
        <span className="text-[10px] text-[#8a978f] font-mono tracking-widest">{v.acriss}</span>
      </div>
      <div className="px-4 pt-2">
        <h3 className="font-bold text-[#0F1E17] leading-snug">{v.name} <span className="text-xs font-normal text-[#57685e]">{t("or similar")}</span></h3>
      </div>

      <div className="relative mt-3 h-44 bg-[#f4f7f4] overflow-hidden">
        <img src={imgSrc(img.url)} alt={`${v.name} — ${img.label}`} className="w-full h-full object-cover" loading="lazy" />
        {images.length > 1 && (
          <>
            <button type="button" onClick={() => setIdx((idx - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 rounded-full p-1.5 hover:bg-white" data-testid={`fleet-gallery-prev-${v.id}`}>
              <ChevronLeft className="w-4 h-4 text-[#144733]" />
            </button>
            <button type="button" onClick={() => setIdx((idx + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 rounded-full p-1.5 hover:bg-white" data-testid={`fleet-gallery-next-${v.id}`}>
              <ChevronRight className="w-4 h-4 text-[#144733]" />
            </button>
          </>
        )}
        <button type="button" onClick={() => setOpen(!open)}
          className="absolute bottom-2 right-2 bg-[#0F1E17]/75 text-white text-[10px] uppercase tracking-wide px-2.5 py-1.5 rounded flex items-center gap-1.5 hover:bg-[#0F1E17]"
          data-testid={`fleet-expand-${v.id}`}>
          <Images className="w-3.5 h-3.5" /> {t("Gallery ({{n}})", { n: images.length })}
        </button>
        <span className="absolute bottom-2 left-2 bg-white/85 text-[#144733] text-[10px] px-2 py-1 rounded">{img.label}</span>
      </div>
      {open && (
        <div className="flex gap-2 px-4 pt-3 overflow-x-auto" data-testid={`fleet-thumbs-${v.id}`}>
          {images.map((im, i) => (
            <button key={im.image_id} type="button" onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${i === idx ? "border-[#009900]" : "border-transparent opacity-70"}`}>
              <img src={imgSrc(im.url)} alt={im.label} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className="p-4 flex-1 flex flex-col">
        <div className="grid grid-cols-4 gap-1 text-[11px] text-[#57685e]">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#009900]" />{t("{{n}} Seats", { n: v.seats })}</span>
          <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5 text-[#009900]" />{v.luggage_large}L + {v.luggage_small}S</span>
          <span className="flex items-center gap-1"><Cog className="w-3.5 h-3.5 text-[#009900]" />{v.transmission.slice(0, 4)}.</span>
          <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5 text-[#009900]" />{v.fuel}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {chips.map((f) => (
            <span key={f} className="bg-[#f0f5f0] text-[#144733] text-[10px] px-2 py-1 rounded-full border border-[#e2ebe4]">{f}</span>
          ))}
          {v.features.length > 4 && (
            <button type="button" onClick={() => setAllChips(!allChips)} className="text-[10px] text-[#009900] font-semibold px-1" data-testid={`fleet-more-chips-${v.id}`}>
              {allChips ? t("less") : t("+{{n}} more", { n: v.features.length - 4 })}
            </button>
          )}
        </div>

        <div className="mt-3 text-[11px] text-[#57685e] border-t border-dashed border-[#E2E8F0] pt-3 space-y-1">
          <div>{t("Security hold")} <strong className="text-[#0F1E17]">AED {v.deposit_cc.toLocaleString()}</strong> {t("(credit)")} · <strong className="text-[#0F1E17]">AED {v.deposit_dc.toLocaleString()}</strong> {t("(debit)")}</div>
          <div>{t("No-deposit rental available")} · {std ? t("Standard") : t("Premium")} · {t("Min age {{age}}", { age: v.min_age })} · {t("Excess AED {{excess}}", { excess: v.excess.toLocaleString() })}</div>
        </div>

        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div className="pl-2 border-l-[3px] border-[#FFDC00]">
            <div className="text-xl font-bold text-[#0F1E17]" data-testid={`fleet-daily-${v.id}`}>{t("From AED {{amt}}", { amt: v.daily_incl.toLocaleString() })}<span className="text-xs font-normal text-[#57685e]"> {t("/ day")}</span></div>
            <div className="text-[10px] text-[#009900] font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {t("CDW Insurance Included")}</div>
            <div className="text-[10px] text-[#57685e] mt-0.5">{t("Weekly AED {{w}} · Monthly AED {{m}} · incl. VAT", { w: v.weekly_incl.toLocaleString(), m: v.monthly_incl.toLocaleString() })}</div>
            <div className="text-[9px] text-[#8a978f]">{t("Indicative — confirm live rate at booking")}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={() => onDetails(v)} className="btn-cta rounded flex-1 py-2.5 text-xs" data-testid={`fleet-details-${v.id}`}>
            {t("View Details")}
          </button>
          <button type="button" onClick={() => onReserve(v)} className="bg-[#144733] text-white rounded flex-1 py-2.5 text-xs font-semibold hover:bg-[#0F1E17] transition-colors" data-testid={`fleet-check-${v.id}`}>
            {t("Reserve")}
          </button>
        </div>
      </div>
    </div>
  );
};
